import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import ServiceCard from "../../components/ServiceCard";
import {
  formatCategories,
  getCategoryDisplayName,
} from "../../utils/categoryFormatter";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const SearchResults = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Search state
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [results, setResults] = useState({
    services: [],
    products: [],
    professionals: [],
    shops: [],
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  // Filter state
  const [filters, setFilters] = useState({
    category: searchParams.get("category") || "",
    priceMin: searchParams.get("priceMin") || "",
    priceMax: searchParams.get("priceMax") || "",
    minRating: searchParams.get("minRating") || "",
    sortBy: searchParams.get("sortBy") || "relevance",
    includeFreelancers: searchParams.get("includeFreelancers") !== "false",
  });

  // Geolocation state
  const [userLocation, setUserLocation] = useState(null);
  const [searchRadius, setSearchRadius] = useState(31); // miles (default ~50 km)
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [locationPermission, setLocationPermission] = useState("prompt"); // prompt, granted, denied

  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (searchQuery) {
      performSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filters, userLocation, searchRadius]);

  // Autocomplete debounce
  useEffect(() => {
    if (searchQuery.length >= 2) {
      const timer = setTimeout(() => {
        fetchSuggestions();
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const fetchSuggestions = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/search/suggestions`,
        {
          params: { q: searchQuery },
        },
      );
      setSuggestions(response.data.suggestions || []);
      setShowSuggestions(true);
    } catch (error) {
    }
  };

  const performSearch = async () => {
    try {
      setLoading(true);
      const params = {
        q: searchQuery,
        type: activeTab === "all" ? "all" : activeTab,
        ...filters,
      };

      if (userLocation) {
        params.lat = userLocation.latitude;
        params.lng = userLocation.longitude;
        params.radius = Math.round(searchRadius * 1.60934); // Convert miles to km for backend
      }

      const response = await axios.get(`${API_BASE_URL}/api/search`, {
        params,
      });

      setResults(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const requestLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setShowLocationPrompt(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLocationPermission("granted");
        setShowLocationPrompt(false);
      },
      (error) => {
        setLocationPermission("denied");
        setShowLocationPrompt(false);
        alert("Unable to get your location. Please enable location services.");
      },
    );
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    performSearch();
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion?.targetUrl) {
      setShowSuggestions(false);
      navigate(suggestion.targetUrl);
      return;
    }
    setSearchQuery(suggestion.text);
    setShowSuggestions(false);
  };

  const getResultCount = () => {
    if (activeTab === "all") return results.total;
    return results[activeTab]?.length || 0;
  };

  const renderServiceCard = (service) => {
    const provider = service.isFreelancer ? service.professional : service.shop;
    const place = [provider?.city, provider?.state].filter(Boolean).join(", ");
    const footnote = [
      service.isFreelancer ? "Freelancer" : null,
      provider?.name,
      place,
    ]
      .filter(Boolean)
      .join(" · ");

    return (
      <ServiceCard
        key={service._id}
        service={service}
        image={service.serviceImage || service.shop?.images?.[0]}
        businessType={service.isFreelancer ? "freelancer" : "salon"}
        footnote={footnote}
        onSelect={() => {
          if (service.isFreelancer && service.professional?._id) {
            // Navigate directly to freelancer booking page with staffId and serviceId
            navigate(
              `/freelancer-booking?staffId=${service.professional._id}&serviceId=${service._id}`,
            );
          } else if (service.shop?.slug && service.shop?.businessId?.slug) {
            // Navigate to booking page with serviceId at specific shop
            const businessSlug = service.shop.businessId.slug;
            const locationSlug = service.shop.slug;
            navigate(
              `/business/${businessSlug}/${locationSlug}/book?serviceId=${service._id}`,
            );
          } else {
            // Fallback to direct service page if slugs not available
            navigate(`/service/${service._id}`);
          }
        }}
      />
    );
  };

  const renderProductCard = (product) => (
    <div
      key={product._id}
      onClick={() => {
        if (product.source === "shop" && product.shop?._id) {
          // Navigate to product page with shopId parameter
          navigate(`/product/${product._id}?shopId=${product.shop._id}`);
        } else {
          navigate(`/product/${product._id}`);
        }
      }}
      style={{
        background: "white",
        borderRadius: "12px",
        padding: isMobile ? "12px" : "16px",
        cursor: "pointer",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        transition: "all 0.2s",
        border: "1px solid #e5e7eb",
        minHeight: "240px",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.12)")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)")
      }
    >
      {product.images?.[0] && (
        <div
          style={{
            width: "100%",
            height: "150px",
            borderRadius: "8px",
            background: `url(${product.images[0]}) center/cover`,
            marginBottom: "12px",
          }}
        />
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
          marginBottom: "8px",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: isMobile ? "13px" : "14px",
            fontWeight: "600",
            color: "#111827",
          }}
        >
          {product.name}
        </h3>
        <span
          style={{
            fontSize: isMobile ? "13px" : "14px",
            fontWeight: "700",
            color: "#2d6da3",
          }}
        >
          ${product.price || 0}
        </span>
      </div>
      <p
        style={{
          margin: "8px 0",
          fontSize: "13px",
          color: "#6b7280",
          lineHeight: "1.5",
        }}
      >
        {product.description?.substring(0, 80)}
        {product.description?.length > 80 ? "..." : ""}
      </p>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap",
          fontSize: "12px",
          color: "#9ca3af",
        }}
      >
        {product.brand && <span>🏷️ {product.brand}</span>}
        {product.category && (
          <span
            style={{
              background: "#eff6ff",
              color: "#2d6da3",
              padding: "2px 8px",
              borderRadius: "12px",
            }}
          >
            {formatCategories(product.category)}
          </span>
        )}
        {product.source === "global" && (
          <span
            style={{
              background: "#dcfce7",
              color: "#065f46",
              padding: "2px 8px",
              borderRadius: "12px",
            }}
          >
            Available at Multiple Locations
          </span>
        )}
      </div>
    </div>
  );

  const renderProfessionalCard = (professional) => {
    return (
      <div
        key={professional._id}
        onClick={() => {
          const isIndependent =
            professional.isFreelancer || ["R4", "R7"].includes(professional.role);
          const independentBasePath =
            professional.role === "R7" ? "/freelancers" : "/freelancer";

          if (isIndependent && professional.slug) {
            navigate(`${independentBasePath}/${professional.slug}`);
          } else if (isIndependent) {
            // Fallback if no slug
            navigate(`${independentBasePath}/${professional._id}`);
          } else if (
            professional.shop?.businessId?.slug &&
            professional.shop?.slug &&
            professional.slug
          ) {
            // Navigate to professional page within shop context
            const businessSlug = professional.shop.businessId.slug;
            const locationSlug = professional.shop.slug;
            navigate(
              `/business/${businessSlug}/${locationSlug}/professional/${professional.slug}`,
            );
          } else {
            navigate(`/professional/${professional._id}`);
          }
        }}
        style={{
          background: "white",
          borderRadius: "12px",
          padding: isMobile ? "12px" : "16px",
          cursor: "pointer",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          transition: "all 0.2s",
          border: "1px solid #e5e7eb",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "12px",
          minHeight: "240px",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.12)")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)")
        }
      >
        <div
          style={{
            width: isMobile ? "80px" : "120px",
            height: isMobile ? "80px" : "120px",
            borderRadius: "50%",
            background:
              professional.userId?.profilePicture || professional.brandImage
                ? `url(${professional.userId?.profilePicture || professional.brandImage}) center/cover`
                : "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "32px",
            flexShrink: 0,
          }}
        >
          {!(professional.userId?.profilePicture || professional.brandImage) &&
            "👨‍💼"}
        </div>
        <div style={{ width: "100%", textAlign: "center" }}>
          <h3
            style={{
              margin: "0 0 4px 0",
              fontSize: isMobile ? "14px" : "16px",
              fontWeight: "600",
              color: "#111827",
            }}
          >
            {professional.name}
          </h3>
          {professional.bio && (
            <p
              style={{
                margin: "4px 0",
                fontSize: "13px",
                color: "#6b7280",
                lineHeight: "1.5",
                textAlign: "left",
              }}
            >
              {professional.bio.substring(0, 100)}
              {professional.bio.length > 100 ? "..." : ""}
            </p>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginTop: "8px",
              fontSize: "12px",
              color: "#9ca3af",
            }}
          >
            {professional.rating && (
              <span style={{ color: "#f59e0b" }}>
                ⭐ {professional.rating.toFixed(1)}
              </span>
            )}
            {professional.isFreelancer && (
              <span
                style={{
                  background: "#fef3c7",
                  color: "#92400e",
                  padding: "2px 8px",
                  borderRadius: "12px",
                }}
              >
                Freelancer
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderShopCard = (shop) => (
    <div
      key={shop._id}
      onClick={() => {
        if (shop.businessId?.slug && shop.slug) {
          const businessSlug = shop.businessId.slug;
          const locationSlug = shop.slug;
          navigate(`/business/${businessSlug}/${locationSlug}`);
        } else {
        }
      }}
      style={{
        background: "white",
        borderRadius: "12px",
        overflow: "hidden",
        cursor: "pointer",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        transition: "all 0.2s",
        border: "1px solid #e5e7eb",
        minHeight: "240px",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.12)")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)")
      }
    >
      {shop.images?.[0] || shop.businessId?.logo?.url ? (
        <div
          style={{
            width: "100%",
            height: "180px",
            background: shop.images?.[0]
              ? `url(${shop.images[0]}) center/cover`
              : "#f3f4f6",
            position: "relative",
            display: shop.images?.[0] ? "block" : "flex",
            alignItems: shop.images?.[0] ? "initial" : "center",
            justifyContent: shop.images?.[0] ? "initial" : "center",
          }}
        >
          {shop.images?.[0] && shop.businessId?.logo?.url && (
            <div
              style={{
                position: "absolute",
                bottom: "8px",
                left: "8px",
                width: "80px",
                height: "80px",
                borderRadius: "8px",
                background: `url(${shop.businessId.logo.url}) center/cover white`,
                border: "2px solid white",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            />
          )}
          {!shop.images?.[0] && shop.businessId?.logo?.url && (
            <img
              src={shop.businessId.logo.url}
              alt={shop.name}
              style={{
                maxWidth: "80%",
                maxHeight: "80%",
                objectFit: "contain",
              }}
            />
          )}
        </div>
      ) : null}
      <div style={{ padding: isMobile ? "12px" : "16px" }}>
        <h3
          style={{
            margin: "0 0 4px 0",
            fontSize: isMobile ? "13px" : "14px",
            fontWeight: "600",
            color: "#111827",
          }}
        >
          {shop.name}
        </h3>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginTop: "8px",
            fontSize: "12px",
            color: "#9ca3af",
          }}
        >
          {shop.rating && (
            <span style={{ color: "#f59e0b" }}>
              ⭐ {shop.rating.toFixed(1)}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <CustomerHeader />
      <div
        style={{
          minHeight: "100vh",
          background: "#f9fafb",
          paddingTop: "80px",
        }}
      >
        {/* Search Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
            color: "white",
            padding: isMobile ? "24px 16px" : "40px 48px",
          }}
        >
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            <h1
              style={{
                fontSize: isMobile ? "20px" : "28px",
                fontWeight: "700",
                marginBottom: "24px",
              }}
            >
              Search Results
            </h1>

            {/* Search Bar with Autocomplete */}
            <form onSubmit={handleSearch} style={{ position: "relative" }}>
              <div style={{ display: "flex", gap: "12px" }}>
                <div style={{ flex: 1, position: "relative" }}>
                  <input
                    type="text"
                    placeholder="Search services, products, professionals, or shops..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() =>
                      suggestions.length > 0 && setShowSuggestions(true)
                    }
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      fontSize: "15px",
                      border: "none",
                      borderRadius: "8px",
                      outline: "none",
                    }}
                  />

                  {/* Autocomplete Suggestions */}
                  {showSuggestions && suggestions.length > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        background: "white",
                        borderRadius: "8px",
                        marginTop: "4px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        zIndex: 1000,
                        maxHeight: "300px",
                        overflowY: "auto",
                      }}
                    >
                      {suggestions.map((suggestion, idx) => (
                        <div
                          key={idx}
                          onClick={() => handleSuggestionClick(suggestion)}
                          style={{
                            padding: "12px 16px",
                            cursor: "pointer",
                            borderBottom:
                              idx < suggestions.length - 1
                                ? "1px solid #e5e7eb"
                                : "none",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = "#f9fafb")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "white")
                          }
                        >
                          <span style={{ color: "#111827" }}>
                            {suggestion.location
                              ? `${suggestion.text}, ${suggestion.location}`
                              : suggestion.text}
                          </span>
                          <span
                            style={{
                              fontSize: "11px",
                              background: "#eff6ff",
                              color: "#2d6da3",
                              padding: "2px 8px",
                              borderRadius: "12px",
                            }}
                          >
                            {suggestion.type}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={requestLocation}
                  disabled={showLocationPrompt}
                  style={{
                    padding: "14px 20px",
                    background:
                      locationPermission === "granted" ? "#10b981" : "white",
                    color:
                      locationPermission === "granted" ? "white" : "#2d6da3",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: showLocationPrompt ? "wait" : "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {showLocationPrompt
                    ? "⏳"
                    : locationPermission === "granted"
                      ? "✓ Near Me"
                      : "📍 Near Me"}
                </button>

                <button
                  type="submit"
                  style={{
                    padding: "14px 32px",
                    background: "#10b981",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Search
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Filters and Results */}
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: isMobile ? "24px 16px" : "40px 48px",
          }}
        >
          {/* Filters Bar */}
          <div
            style={{
              background: "white",
              padding: isMobile ? "16px" : "20px",
              borderRadius: "12px",
              marginBottom: "24px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
                gap: "12px",
              }}
            >
              {/* Price Min */}
              <input
                type="number"
                placeholder="Min Price ($)"
                value={filters.priceMin}
                onChange={(e) => updateFilter("priceMin", e.target.value)}
                style={{
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />

              {/* Price Max */}
              <input
                type="number"
                placeholder="Max Price ($)"
                value={filters.priceMax}
                onChange={(e) => updateFilter("priceMax", e.target.value)}
                style={{
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />

              {/* Rating */}
              <select
                value={filters.minRating}
                onChange={(e) => updateFilter("minRating", e.target.value)}
                style={{
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: "white",
                }}
              >
                <option value="">All Ratings</option>
                <option value="4">4+ Stars</option>
                <option value="4.5">4.5+ Stars</option>
                <option value="5">5 Stars Only</option>
              </select>

              {/* Sort */}
              <select
                value={filters.sortBy}
                onChange={(e) => updateFilter("sortBy", e.target.value)}
                style={{
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: "white",
                }}
              >
                <option value="relevance">Sort: Relevance</option>
                <option value="priceAsc">Price: Low to High</option>
                <option value="priceDesc">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                {userLocation && <option value="distance">Distance</option>}
              </select>
            </div>

            {/* Distance Radius Slider (show only when location enabled) */}
            {userLocation && (
              <div style={{ marginTop: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                  }}
                >
                  Search Radius: {searchRadius} miles (
                  {Math.round(searchRadius * 1.60934)} km)
                </label>
                <input
                  type="range"
                  min="3"
                  max="62"
                  step="3"
                  value={searchRadius}
                  onChange={(e) => setSearchRadius(parseInt(e.target.value))}
                  style={{
                    width: "100%",
                    height: "6px",
                    borderRadius: "5px",
                    background: `linear-gradient(to right, #2d6da3 0%, #2d6da3 ${(searchRadius / 62) * 100}%, #d1d5db ${(searchRadius / 62) * 100}%, #d1d5db 100%)`,
                    outline: "none",
                    appearance: "none",
                    cursor: "pointer",
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "12px",
                    color: "#9ca3af",
                    marginTop: "4px",
                  }}
                >
                  <span>3 miles</span>
                  <span>62 miles</span>
                </div>
              </div>
            )}

            {/* Freelancer Toggle */}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginTop: "12px",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={filters.includeFreelancers}
                onChange={(e) =>
                  updateFilter("includeFreelancers", e.target.checked)
                }
                style={{ width: "18px", height: "18px", cursor: "pointer" }}
              />
              <span>Include Freelancers</span>
            </label>
          </div>

          {/* Result Tabs */}
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: "24px",
              overflowX: "auto",
              paddingBottom: "8px",
            }}
          >
            {[
              { key: "all", label: "All", count: results.total },
              {
                key: "services",
                label: "Services",
                count: results.services?.length || 0,
              },
              {
                key: "products",
                label: "Products",
                count: results.products?.length || 0,
              },
              {
                key: "professionals",
                label: "Professionals",
                count: results.professionals?.length || 0,
              },
              {
                key: "shops",
                label: "Shops",
                count: results.shops?.length || 0,
              },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: "10px 20px",
                  background: activeTab === tab.key ? "#2d6da3" : "white",
                  color: activeTab === tab.key ? "white" : "#6b7280",
                  border: activeTab === tab.key ? "none" : "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: activeTab === tab.key ? "600" : "500",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "all 0.2s",
                }}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          {/* Results Count */}
          <p
            style={{ fontSize: "14px", color: "#6b7280", marginBottom: "20px" }}
          >
            {loading ? "Searching..." : `${getResultCount()} results found`}
          </p>

          {/* Results Grid */}
          {loading ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 20px",
                color: "#9ca3af",
              }}
            >
              <p style={{ fontSize: "16px" }}>Searching...</p>
            </div>
          ) : getResultCount() === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 20px",
                color: "#9ca3af",
              }}
            >
              <p
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#6b7280",
                  marginBottom: "8px",
                }}
              >
                No results found
              </p>
              <p style={{ fontSize: "14px" }}>
                Try adjusting your search or filters
              </p>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "repeat(2, 1fr)"
                  : "repeat(5, 1fr)",
                gap: "16px",
              }}
            >
              {(activeTab === "all" || activeTab === "services") &&
                results.services?.map(renderServiceCard)}
              {(activeTab === "all" || activeTab === "products") &&
                results.products?.map(renderProductCard)}
              {(activeTab === "all" || activeTab === "professionals") &&
                results.professionals?.map(renderProfessionalCard)}
              {(activeTab === "all" || activeTab === "shops") &&
                results.shops?.map(renderShopCard)}
            </div>
          )}
        </div>
      </div>
      <CustomerFooter />
    </>
  );
};

export default SearchResults;